import React, { useState, useEffect, useCallback, useMemo, useRef, memo } from "react";
import { useTrueViewportHeight } from './hooks/useTrueViewportHeight';
import D3Cloud from "./components/D3Cloud";
import ScrollObserver from "./components/ScrollObserver";
import questionData from "./data/questionData";
import FeatureScroll from './components/FeatureScroll';
import ProgressIndicator from './components/ProgressIndicator';
import HeadlineFlow from './components/HeadlineFlow';
import { headlineFlowData } from './data/headlineData';
import "./Styles.css";
import ErrorBoundary from './components/ErrorBoundary';
import NavigationButtons from './components/NavigationButtons';
import { useAppStore } from './store';
import { Analytics } from '@vercel/analytics/react';

// --- CONSTANTS ---
const NUM_QUESTION_SECTIONS = questionData.length;
const NUM_FEATURE_SECTIONS = 9;

// <<< THE NEW, SIMPLIFIED APPROACH >>>
// We define a simple vh amount to scroll for each headline to pass by.
// This is our main "tuning knob" for the scroll feel.
const VH_PER_HEADLINE = 40; 

const createResponsiveSectionHeights = () => {
  const isMobile = window.innerWidth < 768;
  
  return {
    intro: isMobile ? 100 : 150,
    question: isMobile ? 120 : 210,      // Keep this - it worked great!
    returnPhase: isMobile ? 60 : 100,
    neuronalConnectionsTimeline: isMobile ? 300 : 450,
    neuronalConnectionsTrigger: isMobile ? 200 : 350,
    feature: 150,                        // Same on mobile/desktop - no change
    headlineFlowStartTrigger: 1,
    headlineFlowScrollArea: (headlineFlowData.length - 1) * VH_PER_HEADLINE,
  };
};

const SECTION_HEIGHTS_VH = createResponsiveSectionHeights();

// This function is now much simpler as it uses the constant directly.
const calculateSectionStartVh = (): number[] => {
    const vhStarts: number[] = [];
    let currentVh = 0;

    vhStarts[0] = currentVh; currentVh += SECTION_HEIGHTS_VH.intro;
    for (let i = 0; i < NUM_QUESTION_SECTIONS; i++) {
        vhStarts[1 + i] = currentVh; currentVh += SECTION_HEIGHTS_VH.question;
    }
    const lastQuestionSectionIndex = NUM_QUESTION_SECTIONS;
    vhStarts[lastQuestionSectionIndex + 1] = currentVh; currentVh += SECTION_HEIGHTS_VH.returnPhase;
    vhStarts[lastQuestionSectionIndex + 2] = currentVh; currentVh += SECTION_HEIGHTS_VH.neuronalConnectionsTrigger;
    const featureStartIndex = lastQuestionSectionIndex + 3;
    for (let i = 0; i < NUM_FEATURE_SECTIONS; i++) {
        vhStarts[featureStartIndex + i] = currentVh; currentVh += SECTION_HEIGHTS_VH.feature;
    }
    const lastFeatureSectionIndex = featureStartIndex + NUM_FEATURE_SECTIONS - 1;
    const headlineFlowActivationIndex = lastFeatureSectionIndex + 1;
    vhStarts[headlineFlowActivationIndex] = currentVh; currentVh += SECTION_HEIGHTS_VH.headlineFlowStartTrigger;
    
    // The final end marker uses the simple calculated height
    vhStarts[headlineFlowActivationIndex + 1] = currentVh + SECTION_HEIGHTS_VH.headlineFlowScrollArea;
    return vhStarts;
};


// --- REACT COMPONENT ---
const App: React.FC = () => {
  useTrueViewportHeight();
  const isMobile = window.innerWidth < 768;      // For mobile-only logic
  // --- STATE AND REFS ---
  const [scrollY, setScrollY] = useState(0);
  const [headlineProgress, setHeadlineProgress] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(window.innerHeight);
  const [scrollDirection, setScrollDirection] = useState<'down' | 'up'>('down');
  const activeSection = useAppStore((state) => state.activeSection);
const appMode = useAppStore((state) => state.appMode);
const isInteractionReady = useAppStore((state) => state.isInteractionReady);
const storeActions = useAppStore((state) => state.actions);

  const activeSectionRef = useRef(0);
  const scrollYRef = useRef(0);
  const lastScrollYRef = useRef(0);     
const isTicking = useRef(false);    
  
  
  // The section start values are now calculated once and are static.
  const sectionStartVhValues = useMemo(() => calculateSectionStartVh(), []);
  const totalAppHeightVh = sectionStartVhValues[sectionStartVhValues.length - 1];

  // --- HANDLERS AND EFFECTS ---
  const handleScroll = useCallback(() => {
    scrollYRef.current = window.scrollY; // Update ref immediately
  
    if (!isTicking.current) {
      window.requestAnimationFrame(() => {
        // Only run state updates once per frame
        setScrollY(scrollYRef.current);
  
        // Determine scroll direction
        if (scrollYRef.current > lastScrollYRef.current) {
          setScrollDirection('down');
        } else if (scrollYRef.current < lastScrollYRef.current) {
          setScrollDirection('up');
        }
        
        lastScrollYRef.current = scrollYRef.current;
        isTicking.current = false;
      });
      isTicking.current = true;
    }
  }, []);

  const handleResize = useCallback(() => setViewportHeight(window.innerHeight), []);
  const handleHeadlineProgressChange = useCallback((progress: number) => {
    setHeadlineProgress(progress);
}, []);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleResize);
    handleScroll(); handleResize();
    return () => {
        window.removeEventListener("scroll", handleScroll);
        window.removeEventListener("resize", handleResize);
    };
  }, [handleScroll, handleResize]);

  const handleStepChange = useCallback((index: number) => {
    storeActions.setSectionFromScroll(index);
}, [storeActions]);
  
  const handleNeuronalAnimationComplete = useCallback(() => {
    storeActions.completeTransition();
}, [storeActions]);

useEffect(() => {
    
const currentMode = useAppStore.getState().appMode;
    if (currentMode === 'transitioning_to_features') return; // Do nothing during a transition
  
  // This logic helps the app know which mode it's in when scrolling.
    if (activeSection >= 1 && activeSection <= questionData.length) {
        storeActions.setAppMode('answering_questions');
    } else if (activeSection > questionData.length + 2) { // Skips over sections 10 & 11
        storeActions.setAppMode('viewing_features');
    }
}, [activeSection, storeActions]);

  const handlePreviousSection = useCallback(() => {
    if (!isMobile) return;

    if (activeSection > 0) {
        storeActions.setNavigating(true); // 👈 SET NAVIGATING FIRST
        const targetSection = activeSection - 1;
        storeActions.setSectionFromButton(targetSection);
        
        const sectionTopPx = (sectionStartVhValues[targetSection] / 100) * window.innerHeight;
        window.scrollTo({ top: sectionTopPx, behavior: 'auto' });

        setTimeout(() => {
            storeActions.setNavigating(false);
        }, 500);
    }
}, [activeSection, isMobile, storeActions, sectionStartVhValues]);

const handleNextSection = useCallback(() => {
    if (!isMobile) return;
    const lastQuestionSection = questionData.length;

    if (activeSection < lastQuestionSection) {
        storeActions.setNavigating(true); // 👈 SET NAVIGATING FIRST
        const newSection = activeSection + 1;
        storeActions.setSectionFromButton(newSection);
        
        const sectionTopPx = (sectionStartVhValues[newSection] / 100) * window.innerHeight;
        window.scrollTo({ top: sectionTopPx, behavior: 'auto' });

        setTimeout(() => {
            storeActions.setNavigating(false);
        }, 500);
    } else if (activeSection === lastQuestionSection) {
        storeActions.handleNextOnLastQuestion(sectionStartVhValues, window.innerHeight);
    }
}, [activeSection, isMobile, storeActions, sectionStartVhValues]);

  // --- PROGRESS INDICATOR DATA ---
  const featureStartIndexGlobal = NUM_QUESTION_SECTIONS + 3;
  const featureEndIndexGlobal = featureStartIndexGlobal + NUM_FEATURE_SECTIONS - 1;
  const headlineFlowActivationIndexGlobal = featureEndIndexGlobal + 1;
  
  const answeringQuestionsProgressData = questionData.map((c) => ({ id: `q-pill-${c.id}`, color: c.backgroundColor }));
  const findingAnswersProgressData = Array.from({ length: NUM_FEATURE_SECTIONS }).map((_, i) => ({ id: `f-pill-${i}`, color: questionData[i % NUM_QUESTION_SECTIONS]?.backgroundColor || '#cccccc' }));
  const tellingStoriesHeadlineProgressData = Array.from({ length: 9 }).map((_, i) => ({ id: `h-pill-${i}`, color: questionData[i % NUM_QUESTION_SECTIONS]?.backgroundColor || '#dddddd' }));

  const progressIndicatorContainerStyle: React.CSSProperties = {
    position: 'fixed', top: '20px', left: '20px', zIndex: 5,
    display: 'flex', flexDirection: 'column', gap: '10px',
  };
  const featuresStartPx = (sectionStartVhValues[featureStartIndexGlobal] / 100) * viewportHeight;

  // Timing constants to sync with HeadlineFlow
// NOTE: These values should match the constants inside your HeadlineFlow.tsx file.
const FADE_IN_DELAY = 0.15;
const FADE_IN_DURATION = 0.20;
const PAUSE_AFTER_FADE = 0.05;
// This calculates the exact moment the headline cards start scrolling.
const SCROLL_START_PROGRESS = FADE_IN_DELAY + FADE_IN_DURATION + PAUSE_AFTER_FADE; // Result: 0.4

// This remaps the progress value. It will stay at 0 until the headline flow
// begins, and then it will go from 0 to 1 correctly over the remaining duration.
const adjustedHeadlineProgress = Math.max(0, (headlineProgress - SCROLL_START_PROGRESS) / (1 - SCROLL_START_PROGRESS));

  // --- JSX RETURN ---
  return (
    <div className="app" style={{ height: `calc(var(--vh, 1vh) * ${totalAppHeightVh})` }}>
<ErrorBoundary>        <D3Cloud
    questionData={questionData} 
    activeSection={activeSection} 
    scrollY={scrollY}
    onCloudExitComplete={handleNeuronalAnimationComplete} // <<< ADD THIS PROP
    neuronalSectionStartVh={sectionStartVhValues[11]} 
    neuronalSectionHeightVh={SECTION_HEIGHTS_VH.neuronalConnectionsTimeline}
    scrollDirection={scrollDirection} 
    onNeuronalAnimationComplete={handleNeuronalAnimationComplete}
/>
        <div style={progressIndicatorContainerStyle}>
            <ProgressIndicator
                title="Questions" sections={answeringQuestionsProgressData} activeSectionGlobal={activeSection} startSectionGlobalIndex={1}
                endSectionGlobalIndex={NUM_QUESTION_SECTIONS + 2} visibilityAndTrackingStartIndex={0} progressMode="perSection"
            />
            <ProgressIndicator
    title="Answers"
    sections={findingAnswersProgressData}
    activeSectionGlobal={activeSection}
    startSectionGlobalIndex={featureStartIndexGlobal}
    endSectionGlobalIndex={featureEndIndexGlobal}
    // 1. APPEAR EARLIER (BUT INVISIBLY):
    // We now render it during the section *before* the first feature, giving it time to be ready.
    visibilityAndTrackingStartIndex={featureStartIndexGlobal - 1}
    // 2. FADE IN SMOOTHLY:
    // It will be active and visible only during its own "chapter" (from the first to the last feature).
    // This also gives us the bonus of it fading out automatically when we reach the Stories section.
    isActive={scrollY >= featuresStartPx - 100}
    progressMode="perSection"
/>
            <ProgressIndicator
    title="Stories"
    sections={tellingStoriesHeadlineProgressData}
    activeSectionGlobal={activeSection}
    startSectionGlobalIndex={headlineFlowActivationIndexGlobal}
    endSectionGlobalIndex={headlineFlowActivationIndexGlobal}
    visibilityAndTrackingStartIndex={featureEndIndexGlobal}
    // 1. FIX FADE-IN TIMING:
    // The indicator now fades in only when you've actually started scrolling
    // through the HeadlineFlow section, not when the trigger is touched.
    isActive={headlineProgress > 0.01}
    progressMode="overall"
    // 2. FIX PILL FILLING TIMING:
    // The pills now fill based on the adjusted progress, which only starts
    // after the transition card's animation is complete.
    overallProgress={adjustedHeadlineProgress}
/>
        </div>
        <FeatureScroll
    scrollY={scrollY}
    activeSection={activeSection}
    viewportHeight={viewportHeight}
    sectionStartVhValues={sectionStartVhValues}
    isVisible={appMode === 'viewing_features' || appMode === 'transitioning_to_features'}
    isInteractionReady={isInteractionReady}
/>
        {headlineFlowData.length > 0 && viewportHeight > 0 && (
            <HeadlineFlow
                scrollY={scrollY} activeSection={activeSection}
                thisFlowSectionIndex={headlineFlowActivationIndexGlobal}
                viewportHeight={viewportHeight} sectionStartVhValues={sectionStartVhValues}
                sectionHeightVh={SECTION_HEIGHTS_VH.headlineFlowScrollArea}
                onProgressChange={handleHeadlineProgressChange}
            />
        )}
        <div className="scroll-container" style={{ position: "relative", zIndex: 2 }}>
        <ScrollObserver
    onStepChange={handleStepChange}
    rootMargin="-5% 0px -5% 0px" // Restore original value
    ignoreIndex={10}
>
                <div className="intro-trigger" style={{ height: `${SECTION_HEIGHTS_VH.intro}vh` }}></div>
                {questionData.map((_, index) => (
                    <div key={`question-trigger-${index + 1}`} className="question-trigger" style={{ height: `${SECTION_HEIGHTS_VH.question}vh` }}></div>
                ))}
                <div className="scroll-trigger post-questions" style={{ height: `${SECTION_HEIGHTS_VH.returnPhase}vh` }} />
                <div className="scroll-trigger neuronal-connections-trigger" style={{ height: `${SECTION_HEIGHTS_VH.neuronalConnectionsTrigger}vh` }} />
                {Array.from({ length: NUM_FEATURE_SECTIONS }).map((_, i) => (
                    <div key={`feature-trigger-${NUM_QUESTION_SECTIONS + 3 + i}`} className={`scroll-trigger feature-trigger-${NUM_QUESTION_SECTIONS + 3 + i}`}
                        style={{ height: `${SECTION_HEIGHTS_VH.feature}vh` }} />
                ))}
                <div key="headline-flow-activate-trigger" className="scroll-trigger headline-flow-activate" style={{ height: `${SECTION_HEIGHTS_VH.headlineFlowStartTrigger}vh` }} />
            </ScrollObserver>
            <div className="headline-flow-scroll-area" style={{ height: `${SECTION_HEIGHTS_VH.headlineFlowScrollArea}vh` }} />
        </div>
        <NavigationButtons
    currentSection={activeSection}
    totalSections={questionData.length + 1} // Allows the jump button
    onPrevious={handlePreviousSection}
    onNext={handleNextSection}
    isVisible={activeSection >= 0 && activeSection <= questionData.length} // Shows through section 9
/>
        <Analytics />         
        </ErrorBoundary>
    </div>
  );
};

export default memo(App);